import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import { PlaylistUrlInput } from './PlaylistUrlInput';
import { useAppContext } from '../context/AppContext';
import { ParseResult, Platform } from '../types';
import { parsePlaylistUrl } from '../services/playlistService';
import { fetchTelemetryStats } from '../services/telemetryService';

interface LandingPageProps {}

const HEADLINE_LINE1 = 'Your music,';
const HEADLINE_LINE2 = 'visualized by emotion';

const EXAMPLE_PLAYLISTS: { name: string; platform: Platform; url: string }[] = [
  { name: "Today's Top Hits", platform: 'spotify', url: 'https://open.spotify.com/playlist/37i9dQZF1DXcBWIGoYBM5M' },
  { name: 'Chill Vibes', platform: 'spotify', url: 'https://open.spotify.com/playlist/37i9dQZF1DX4WYpdgoIcn6' },
  { name: 'Yearning', platform: 'ytmusic', url: 'https://music.youtube.com/playlist?list=RDCLAK5uy_nU67GE7pONSBJf0BR9n9bb_wp517NO6wo' },
];

const LandingPage: React.FC<LandingPageProps> = () => {
  const navigate = useNavigate();
  const { analyzePlaylist, isAnalyzing, analysisProgress, analysisStep, error } = useAppContext();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Typing animation state
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [typingDone, setTypingDone] = useState(false);
  const [playlistCount, setPlaylistCount] = useState<number | null>(null);
  const fullText = HEADLINE_LINE1 + '\n' + HEADLINE_LINE2;

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i < fullText.length) {
        setDisplayText(fullText.slice(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        // Stop blinking, turn cursor into a period
        setTimeout(() => {
          setShowCursor(false);
          setTypingDone(true);
        }, 1500);
      }
    }, 45);
    return () => clearInterval(timer);
  }, []);

  // The playlist currently being submitted — used to build the shareable
  // /results/:playlistId URL once the fetch succeeds.
  const [pending, setPending] = useState<ParseResult | null>(null);

  // Stay on the landing page through the initial "fetching" step so a bad URL,
  // private playlist, or fetch failure surfaces inline here (see error block below)
  // instead of pushing the user to an empty /results screen. Once the playlist
  // fetch succeeds and analysis moves past 'fetching', the effect below navigates.
  const handlePlaylistSubmit = async (result: ParseResult) => {
    setPending(result);
    await analyzePlaylist(result);
  };

  useEffect(() => {
    if (isAnalyzing && analysisStep !== 'fetching' && pending) {
      const query = pending.platform === 'ytmusic'
        ? '?platform=ytmusic'
        : pending.platform === 'apple'
          ? `?platform=apple&sf=${pending.storefront || 'us'}`
          : '';
      navigate(`/results/${encodeURIComponent(pending.playlistId)}${query}`);
    }
  }, [isAnalyzing, analysisStep, navigate, pending]);

  // Fetch social proof stats on mount
  useEffect(() => {
    fetchTelemetryStats().then(stats => {
      if (stats) setPlaylistCount(stats.totalPlaylistsAnalyzed);
    });
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.default,
        color: theme.palette.text.primary,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        px: 3,
        py: 8,
      }}
    >
      {/* Animated lava lamp background */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.4 : 0.55,
          pointerEvents: 'none',
          filter: 'blur(40px)',
          '& svg': {
            width: '100%',
            height: '100%',
          },
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice">
          <defs>
            {/* Animated gradient blobs — indigo/emerald tinted in dark, warm in light */}
            <radialGradient id="blob1" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#3B82F6' : '#a8e6cf'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="8s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#3B82F6' : '#a8e6cf'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="blob2" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#4338CA' : '#ffe66d'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="10s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#4338CA' : '#ffe66d'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="blob3" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#6366F1' : '#ffaa66'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="12s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#6366F1' : '#ffaa66'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="blob4" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#1E1B4B' : '#ff8b94'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="9s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#1E1B4B' : '#ff8b94'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="blob5" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#2563EB' : '#4ecdc4'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="11s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#2563EB' : '#4ecdc4'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="blob6" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#7C3AED' : '#dda8ff'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="13s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#7C3AED' : '#dda8ff'} stopOpacity="0" />
            </radialGradient>
          </defs>
          
          {/* Animated blob circles */}
          <circle fill="url(#blob1)" r="220">
            <animate attributeName="cx" values="200;800;200" dur="25s" repeatCount="indefinite" />
            <animate attributeName="cy" values="300;200;300" dur="20s" repeatCount="indefinite" />
            <animate attributeName="r" values="220;300;220" dur="15s" repeatCount="indefinite" />
          </circle>
          
          <circle fill="url(#blob2)" r="280">
            <animate attributeName="cx" values="800;300;800" dur="30s" repeatCount="indefinite" />
            <animate attributeName="cy" values="400;700;400" dur="25s" repeatCount="indefinite" />
            <animate attributeName="r" values="280;200;280" dur="18s" repeatCount="indefinite" />
          </circle>
          
          <circle fill="url(#blob3)" r="250">
            <animate attributeName="cx" values="500;700;500" dur="28s" repeatCount="indefinite" />
            <animate attributeName="cy" values="600;300;600" dur="22s" repeatCount="indefinite" />
            <animate attributeName="r" values="250;330;250" dur="16s" repeatCount="indefinite" />
          </circle>
          
          <circle fill="url(#blob4)" r="200">
            <animate attributeName="cx" values="300;600;300" dur="32s" repeatCount="indefinite" />
            <animate attributeName="cy" values="200;600;200" dur="27s" repeatCount="indefinite" />
            <animate attributeName="r" values="200;270;200" dur="19s" repeatCount="indefinite" />
          </circle>
          
          <circle fill="url(#blob5)" r="240">
            <animate attributeName="cx" values="700;200;700" dur="26s" repeatCount="indefinite" />
            <animate attributeName="cy" values="700;400;700" dur="24s" repeatCount="indefinite" />
            <animate attributeName="r" values="240;310;240" dur="17s" repeatCount="indefinite" />
          </circle>
          
          <circle fill="url(#blob6)" r="210">
            <animate attributeName="cx" values="400;900;400" dur="29s" repeatCount="indefinite" />
            <animate attributeName="cy" values="500;500;500" dur="23s" repeatCount="indefinite" />
            <animate attributeName="r" values="210;290;210" dur="20s" repeatCount="indefinite" />
          </circle>
        </svg>
      </Box>
      
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '800px', width: '100%' }}>
        <>
        {/* Platform badges */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 4,
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: '100px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              fontSize: '12px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#1DB954">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            Spotify
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: '100px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              fontSize: '12px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF0000">
              <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L16.2 12l-6.516 3.54z"/>
            </svg>
            YouTube Music
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: '100px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              fontSize: '12px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 384 512" fill={theme.palette.text.secondary}>
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            Apple Music
          </Box>
        </Box>

        {/* Main heading with typing animation */}
        <Box sx={{ position: 'relative', mb: 5 }}>
          {/* Invisible full text to hold layout width */}
          <Typography
            variant="h1"
            aria-hidden="true"
            sx={{
              fontSize: { xs: '48px', sm: '64px', md: '72px' },
              fontWeight: 700,
              lineHeight: 1.1,
              color: 'transparent',
              letterSpacing: '-0.02em',
              userSelect: 'none',
            }}
          >
            {HEADLINE_LINE1}<br />{HEADLINE_LINE2}.
          </Typography>
          {/* Typing overlay */}
          <Typography
            variant="h1"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              fontSize: { xs: '48px', sm: '64px', md: '72px' },
              fontWeight: 700,
              lineHeight: 1.1,
              color: theme.palette.text.primary,
              letterSpacing: '-0.02em',
            }}
          >
            {displayText.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
            {showCursor && (
              <Box
                component="span"
                sx={{
                  borderRight: `3px solid ${theme.palette.text.primary}`,
                  ml: '1px',
                  animation: 'blink 0.7s step-end infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0 },
                  },
                }}
              >&nbsp;</Box>
            )}
            {typingDone && !showCursor && (
              <Box component="span">.</Box>
            )}
          </Typography>
        </Box>

        {/* Playlist URL Input */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <PlaylistUrlInput onSubmit={handlePlaylistSubmit} isLoading={isAnalyzing} progressMessage={analysisProgress} />
          
          {/* Error message */}
          {error && (
            <Typography
              sx={{
                mt: 2,
                fontSize: '13px',
                color: '#EF4444',
                fontWeight: 500,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>
        
        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            maxWidth: '500px',
            mx: 'auto',
            mb: 5,
          }}
        >
          Discover the emotional landscape of any public playlist. No login required.
        </Typography>

        {/* Example playlists */}
        <Box sx={{ mb: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: theme.palette.text.secondary,
            }}
          >
            Try an example:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {EXAMPLE_PLAYLISTS.map((example) => (
              <Box
                key={example.url}
                component="button"
                onClick={async () => {
                  const result = parsePlaylistUrl(example.url);
                  if (result) {
                    // Navigation is handled by the effect above once fetch succeeds.
                    setPending(result);
                    await analyzePlaylist(result);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 2,
                  py: 0.75,
                  borderRadius: '100px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  '&:hover': {
                    background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'scale(0.96)',
                  },
                }}
              >
                {example.platform === 'spotify' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#1DB954">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF0000">
                    <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L16.2 12l-6.516 3.54z"/>
                  </svg>
                )}
                {example.name}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Social proof counter */}
        {playlistCount !== null && (
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              mb: 3,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
            <span><strong>{new Intl.NumberFormat().format(playlistCount)}</strong> playlists analyzed</span>
          </Typography>
        )}

        {/* Navigation buttons */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            mt: 6,
          }}
        >
          <Box
            component="button"
            onClick={() => navigate('/about')}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: '100px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              fontSize: '14px',
              color: theme.palette.text.secondary,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontFamily: 'inherit',
              '&:hover': {
                background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
              },
            }}
          >
            <InfoIcon sx={{ fontSize: 18 }} />
            About
          </Box>
          <Box
            component="button"
            onClick={() => navigate('/contact')}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: '100px',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              fontSize: '14px',
              color: theme.palette.text.secondary,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontFamily: 'inherit',
              '&:hover': {
                background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
              },
            }}
          >
            <EmailIcon sx={{ fontSize: 18 }} />
            Contact
          </Box>
        </Box>
        </>
      </Box>
    </Box>
  );
};

export default LandingPage;
